import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Briefcase,
  MapPin,
  Clock,
  ArrowRight,
  Heart,
  GraduationCap,
  DollarSign,
  Users,
  Coffee,
  Plane,
  Mail,
  Send,
} from 'lucide-react';
import { Container, Section, Badge, Card, Button, ApplyModal } from '../components/ui';
import { CTA, PageHero } from '../components/sections';
import { CONTACT_INFO } from '../constants';

type ApplyTarget =
  // Locked-role case: user clicked "Apply Now" on a specific opening.
  | { mode: 'preset'; role: string; department: string }
  // Open-ended case: user clicked the hero "Apply for a Role" button.
  | { mode: 'generic' };

const benefits = [
  {
    icon: DollarSign,
    title: 'Competitive Salary',
    description: 'Industry-leading compensation packages with regular performance reviews.',
  },
  {
    icon: Heart,
    title: 'Health & Wellness',
    description: 'Comprehensive health insurance, mental health support, and gym memberships.',
  },
  {
    icon: GraduationCap,
    title: 'Learning & Development',
    description: 'Annual learning budget, conference attendance, and certification support.',
  },
  {
    icon: Plane,
    title: 'Flexible Time Off',
    description: 'Generous PTO policy, parental leave, and remote work flexibility.',
  },
  {
    icon: Users,
    title: 'Team Events',
    description: 'Regular team outings, hackathons, and company-wide celebrations.',
  },
  {
    icon: Coffee,
    title: 'Modern Workplace',
    description: 'State-of-the-art offices with free snacks, beverages, and ergonomic setups.',
  },
];

interface Opening {
  id: number;
  title: string;
  department: string;
  location: string;
  type: 'Full-Time' | 'Contract';
  experience: string;
  closed: boolean;
}

const openings: Opening[] = [
  {
    id: 1,
    title: 'Senior Full Stack Developer',
    department: 'Engineering',
    location: 'Remote',
    type: 'Full-Time',
    experience: '5+ years',
    closed: true,
  },
  {
    id: 2,
    title: 'GenAI / ML Engineer',
    department: 'AI & Data Science',
    location: 'Remote',
    type: 'Full-Time',
    experience: '3+ years',
    closed: true,
  },
  {
    id: 3,
    title: 'Cloud Solutions Architect',
    department: 'Cloud Engineering',
    location: 'Remote',
    type: 'Full-Time',
    experience: '7+ years',
    closed: true,
  },
  {
    id: 4,
    title: 'Product Designer',
    department: 'Design',
    location: 'Remote',
    type: 'Full-Time',
    experience: '4+ years',
    closed: true,
  },
  {
    id: 5,
    title: 'DevOps Engineer',
    department: 'Engineering',
    location: 'Remote',
    type: 'Full-Time',
    experience: '3+ years',
    closed: true,
  },
  {
    id: 6,
    title: 'Salesforce Developer',
    department: 'Salesforce Practice',
    location: 'Remote',
    type: 'Contract',
    experience: '2+ years',
    closed: true,
  },
];

export const Careers: React.FC = () => {
  const [applyTarget, setApplyTarget] = useState<ApplyTarget | null>(null);
  const roleOptions = openings.map((o) => o.title);

  return (
    <>
      <ApplyModal
        isOpen={applyTarget !== null}
        onClose={() => setApplyTarget(null)}
        role={applyTarget?.mode === 'preset' ? applyTarget.role : undefined}
        department={applyTarget?.mode === 'preset' ? applyTarget.department : undefined}
        roleOptions={applyTarget?.mode === 'generic' ? roleOptions : undefined}
      />
      {/* Hero Section */}
      <PageHero
        badge={
          <Badge className="mb-6 bg-white/10 text-white border border-white/20">
            Join Our Team
          </Badge>
        }
        title="Build the Future of Technology With Us"
        subtitle="Join a team of passionate innovators working on cutting-edge projects for clients around the world."
        contentClassName="-mt-10"
      >
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Button
            variant="accent"
            size="lg"
            onClick={() => setApplyTarget({ mode: 'generic' })}
            leftIcon={<Send className="w-4 h-4" />}
          >
            Apply for a Role
          </Button>
          <a href="#openings">
            <Button
              variant="ghost"
              size="lg"
              className="text-white hover:bg-white/10 border border-white/20"
              rightIcon={<ArrowRight className="w-4 h-4" />}
            >
              Browse Open Positions
            </Button>
          </a>
        </div>
      </PageHero>

      {/* Why Join Us */}
      <Section background="white" padding="lg">
        <Container>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <Badge variant="accent" className="mb-4">Why ISTATEC</Badge>
            <h2 className="heading-lg text-navy-900 mb-4">
              Great Benefits, Greater Impact
            </h2>
            <p className="text-body text-lg">
              We invest in our people because they're our greatest asset. 
              Here's what you can expect when you join our team.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card hover className="h-full">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-navy-100 to-accent-50 flex items-center justify-center mb-4">
                    <benefit.icon className="w-6 h-6 text-navy-700" />
                  </div>
                  <h3 className="text-lg font-semibold text-navy-900 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-navy-600 text-sm">
                    {benefit.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Culture Section */}
      <Section background="mesh" padding="lg">
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge variant="primary" className="mb-4">Our Culture</Badge>
              <h2 className="heading-lg text-navy-900 mb-6">
                Where Innovation Meets Collaboration
              </h2>
              <p className="text-body text-lg mb-6">
                At ISTATEC, we foster a culture of continuous learning, open communication, 
                and mutual respect. We believe that diverse perspectives lead to better solutions.
              </p>
              <ul className="space-y-4">
                {[
                  'Collaborative environment with cross-functional teams',
                  'Regular knowledge sharing sessions and tech talks',
                  'Flat hierarchy where every voice matters',
                  'Focus on work-life balance and flexibility',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-navy-700">
                    <span className="w-2 h-2 rounded-full bg-accent-500" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                { number: '150+', label: 'Team Members' },
                { number: '15+', label: 'Countries' },
                { number: '4.8', label: 'Glassdoor Rating' },
                { number: '92%', label: 'Retention Rate' },
              ].map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="text-center">
                    <div className="text-3xl font-bold text-navy-900 mb-1">
                      {stat.number}
                    </div>
                    <div className="text-sm text-navy-600">{stat.label}</div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* Open Positions */}
      <Section background="white" padding="lg" id="openings">
        <Container>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <Badge variant="accent" className="mb-4">Open Positions</Badge>
            <h2 className="heading-lg text-navy-900 mb-4">
              Find Your Next Opportunity
            </h2>
            <p className="text-body text-lg">
              We're always looking for talented individuals to join our growing team.
            </p>
          </div>

          <div className="space-y-4">
            {openings.map((job, index) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <Card hover={!job.closed} className={job.closed ? 'group opacity-75' : 'group'}>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3
                          className={`text-lg font-semibold ${
                            job.closed
                              ? 'text-navy-500'
                              : 'text-navy-900 group-hover:text-accent-600'
                          } transition-colors`}
                        >
                          {job.title}
                        </h3>
                        <Badge
                          variant={job.type === 'Full-Time' ? 'accent' : 'primary'}
                          className="text-xs"
                        >
                          {job.type}
                        </Badge>
                        {job.closed && (
                          <Badge variant="neutral" className="text-xs">
                            Closed
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-navy-600">
                        <span className="flex items-center gap-1">
                          <Briefcase className="w-4 h-4" />
                          {job.department}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {job.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {job.experience}
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={job.closed}
                      title={job.closed ? 'Applications for this role are closed' : undefined}
                      onClick={() =>
                        setApplyTarget({
                          mode: 'preset',
                          role: job.title,
                          department: job.department,
                        })
                      }
                      rightIcon={
                        !job.closed ? (
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        ) : undefined
                      }
                    >
                      Apply Now
                    </Button>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Don't See Your Role */}
          <Card className="mt-8 bg-navy-900 text-white text-center">
            <h3 className="text-xl font-semibold mb-2">Don't See Your Role?</h3>
            <p className="text-navy-200 mb-4">
              We're always looking for talented people. Send us your resume and we'll 
              keep you in mind for future opportunities.
            </p>
            <a href={`mailto:${CONTACT_INFO.email}?subject=General Application - Career Opportunity&body=Hi ISTATEC Team,%0D%0A%0D%0AI am interested in joining your team. Please find my details below:%0D%0A%0D%0AName:%0D%0ACurrent Role:%0D%0AExperience:%0D%0ALinkedIn:%0D%0A%0D%0AThank you!`}>
              <Button variant="accent" leftIcon={<Mail className="w-4 h-4" />}>
                Send General Application
              </Button>
            </a>
          </Card>
        </Container>
      </Section>

      <CTA />
    </>
  );
};

